/**
 * Störungen-Liste - Legacy-Look
 * PR-8b: Minimal renderbar mit Legacy-Struktur
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { ModuleBadge } from '../components/ModuleBadge';
import {
  AlertTriangle,
  RefreshCw,
  Clock,
  CheckCircle,
  Search,
  ChevronRight,
} from 'lucide-react';
import { formatBerlinDateDE, formatBerlinDateISO } from '../utils/berlinDate';

interface Disruption {
  id: string;
  shiftDate: string;
  shiftType: string;
  startedAt: string;
  endedAt: string | null;
  categoryNameSnapshot: string | null;
  category: { name: string } | null;
  description: string | null;
  techStatus: string;
  machine: { name: string } | null;
  station: { name: string } | null;
}

// Helper: Kategorie-Name aus Snapshot oder Relation
const getCategoryName = (d: Disruption): string => {
  return d.categoryNameSnapshot ?? d.category?.name ?? '–';
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  OPEN: { label: 'Offen', color: 'bg-red-100 text-red-800' },
  IN_PROGRESS: { label: 'In Bearbeitung', color: 'bg-yellow-100 text-yellow-800' },
  WAITING_PARTS: { label: 'Warte auf Teile', color: 'bg-orange-100 text-orange-800' },
  RESOLVED_CONFIRMED: { label: 'Bestätigt', color: 'bg-green-100 text-green-800' },
  REOPENED: { label: 'Wieder geöffnet', color: 'bg-red-100 text-red-800' },
};

const SHIFT_LABELS: Record<string, string> = {
  EARLY: 'Früh',
  LATE: 'Spät',
  SATURDAY: 'Sa',
};

export default function DisruptionsListPage() {
  const [disruptions, setDisruptions] = useState<Disruption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadDisruptions = useCallback(() => {
    setLoading(true);
    fetch('/api/disruptions/active', { credentials: 'include' })
      .then(res => res.ok ? res.json() : [])
      .then(setDisruptions)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadDisruptions();
  }, [loadDisruptions]);

  const handleResolve = async (id: string) => {
    if (!confirm('Störung als behoben markieren?')) return;
    
    setActionLoading(id);
    try {
      const res = await fetch(`/api/disruptions/${id}/end`, {
        method: 'PATCH',
        credentials: 'include',
      });
      if (res.ok) {
        loadDisruptions();
      } else {
        const data = await res.json();
        alert(data.error || 'Fehler beim Markieren als behoben');
      }
    } catch {
      alert('Verbindungsfehler');
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = disruptions.filter(d => {
    if (statusFilter !== 'all' && d.techStatus !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        d.description?.toLowerCase().includes(s) ||
        d.machine?.name.toLowerCase().includes(s) ||
        d.station?.name.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const openCount = disruptions.filter(d => d.techStatus === 'OPEN' || d.techStatus === 'REOPENED').length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header - Legacy Style */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
              Störungen
              <ModuleBadge module="disruptions" />
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {openCount > 0 ? `${openCount} offene Störungen` : 'Keine offenen Störungen'}
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              Offen
            </div>
            <p className="text-2xl font-bold text-red-600">{openCount}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Clock className="w-4 h-4" />
              Heute
            </div>
            <p className="text-2xl font-bold">
              {disruptions.filter(d => d.shiftDate === formatBerlinDateISO(new Date())).length}
            </p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Clock className="w-4 h-4" />
              7 Tage
            </div>
            <p className="text-2xl font-bold">{disruptions.length}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Behoben
            </div>
            <p className="text-2xl font-bold text-green-600">
              {disruptions.filter(d => d.techStatus === 'RESOLVED_CONFIRMED').length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg bg-white"
          >
            <option value="all">Alle Status</option>
            <option value="OPEN">Offen</option>
            <option value="IN_PROGRESS">In Bearbeitung</option>
            <option value="WAITING_PARTS">Warte auf Teile</option>
            <option value="RESOLVED_CONFIRMED">Bestätigt</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Laden...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {search || statusFilter !== 'all' ? 'Keine Treffer' : 'Keine Störungen vorhanden'}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Datum</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Maschine</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Kategorie</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Beschreibung</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(d => (
                  <tr key={d.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium">
                        {formatBerlinDateDE(d.shiftDate).slice(0, 6)}
                      </div>
                      <div className="text-xs text-slate-500">{SHIFT_LABELS[d.shiftType] || d.shiftType}</div>
                    </td>
                    <td className="px-4 py-3 font-medium">{d.machine?.name || '–'}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs rounded bg-slate-100">
                        {getCategoryName(d)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded ${STATUS_CONFIG[d.techStatus]?.color || 'bg-slate-100'}`}>
                        {STATUS_CONFIG[d.techStatus]?.label || d.techStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs truncate">
                      {d.description || '–'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {!d.endedAt && d.techStatus !== 'RESOLVED_CONFIRMED' && (
                          <button
                            onClick={() => handleResolve(d.id)}
                            disabled={actionLoading === d.id}
                            className="px-2 py-1 text-xs font-medium bg-green-500 text-white hover:bg-green-600 rounded disabled:opacity-50"
                          >
                            {actionLoading === d.id ? '...' : 'Behoben'}
                          </button>
                        )}
                        <Link
                          to={`/reports/disruptions?focusId=${d.id}`}
                          className="p-1 text-slate-400 hover:text-slate-600"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
